/// <amd-module name="Release/WorkItemReleaseLinks/ReleaseLinkTypeService" />
import * as React from "react";
import * as Resources from "Release/WorkItemReleaseLinks/Resources";
import { ArtifactsUpdatedCallback } from "Wit/WorkItemLinksRegistration/IRegisteredLinkTypeService";
import { BaseRegisteredLinkTypeService } from "Wit/WorkItemLinksRegistration/BaseRegisteredLinkTypeService";
import { getLinkTypeDisplayName } from "Wit/WorkItemLinksRegistration/LinkUtils";
import { getReleaseClient, IReleaseRestClient } from "Release/Client/RestClient/Release";
import { IReleaseLinkStatutsTextService } from "./ReleaseLinkStatutsTextService";
import { IResolvedLinkDisplayData, ILinkIdentifier } from "Wit/WorkItemLinksRegistration/Contracts";
import { IVssLocationService } from "VSS/Platform/Location";
import { ReleaseEnvironment } from "Release/Client/Contracts/Release";
import { ReleaseState} from "./ReleaseState";
import { Services } from "VSS/Platform/Context";

const IReleaseLinkTypeServiceID = "release-link-type-service";

const releaseManagementToolId = "ReleaseManagement";
const releaseEnvironmentLinkType = "Integrated in release environment";
const releaseLinksDocumentationHref = "https://docs.microsoft.com/en-us/azure/devops/boards/work-items/work-item-deployments-control";

class ReleaseLinkTypeService extends BaseRegisteredLinkTypeService {
    private _releaseClient: IReleaseRestClient | undefined;

    private get releaseClient() {
        if (!this._releaseClient) {
            this._releaseClient = getReleaseClient(this.pageContext);
        }
        return this._releaseClient;
    }

    public getToolId(): string {
        return releaseManagementToolId;
    }

    public renderLinkTypeForm(tool: string, onArtifactsUpdated: ArtifactsUpdatedCallback): JSX.Element {
        onArtifactsUpdated([]); // Clear out any existing artifacts

        return (
            <a href={releaseLinksDocumentationHref} target="_blank">
                {Resources.LinkToReleaseInstructions}
            </a>
        );
    }

    public renderState(link: IResolvedLinkDisplayData) {
        return <ReleaseState link={link} />;
    }

    public resolveArtifacts(linkIdentifiers: ILinkIdentifier[], workItemId: number): Promise<IResolvedLinkDisplayData[]>[] {
        return linkIdentifiers.map(async linkIdentifier => {
            const info = getReleaseInfo(linkIdentifier);
            if (!info) {
                return Promise.resolve([]);
            }
            const releaseEnvironment = await this.releaseClient.getReleaseEnvironment(info.projectId, info.releaseId, info.environmentId);
            return [this.getDisplayData(releaseEnvironment, linkIdentifier, info.projectId)];
        });
    }

    private getDisplayData(releaseEnvironment: ReleaseEnvironment, linkIdentifier: ILinkIdentifier, projectId: string): IResolvedLinkDisplayData {
        const locationService = this.pageContext.getService<IVssLocationService>("IVssLocationService");
        let href: string;
        try {
            href = locationService.routeUrl("ms.vss-releaseManagement-web.cd-release-progress-default-route", {
                project: projectId,
                releaseId: "" + releaseEnvironment.release.id,
                _a: "release-summary"
            });
        } catch (e: unknown) {
            console.error("Error gettting release href", e);
            href = "#";
        }

        const releaseLinkStatutsTextService = this.pageContext.getService<IReleaseLinkStatutsTextService>("IReleaseLinkStatutsTextService");

        return {
            linkIdentifier,
            icons: [
                {
                    iconName: "Deploy",
                },
            ],
            id: releaseEnvironment.id,
            displayId: getLinkTypeDisplayName(releaseEnvironmentLinkType), // We show "Integrated in release stage" as the ID.
            title: `${releaseEnvironment.release.name}/${releaseEnvironment.name}`,
            state: releaseLinkStatutsTextService.getStatusToolTipText(releaseEnvironment),
            updateDate: linkIdentifier.LastWriteDate,
            uri: href,
            tool: releaseManagementToolId,
            linkTypeDisplayName: getLinkTypeDisplayName(releaseEnvironmentLinkType),
            registeredLinkType: releaseEnvironmentLinkType,
            additionalData: { releaseEnvironment },
        };
    }
}

const releaseFilePathPattern =
    /^vstfs:\/\/\/ReleaseManagement\/ReleaseEnvironment\/(?<projectId>[a-z0-9\-]+)\:(?<releaseId>[0-9]+)\:(?<environmentId>[0-9]+)$/i;

interface RawReleaseInfo {
    projectId: string;
    releaseId: string;
    environmentId: string;
}

interface ReleaseInfo {
    projectId: string;
    releaseId: number;
    environmentId: number;
}

interface ReleaseCaptureGroups {
    groups: RawReleaseInfo;
}

function getReleaseInfo(linkIdentifier: ILinkIdentifier): ReleaseInfo | undefined {
    if (!linkIdentifier.FilePath) {
        return undefined;
    }
    const match = releaseFilePathPattern.exec(linkIdentifier.FilePath) as (RegExpExecArray & ReleaseCaptureGroups) | null;
    if (match === null) {
        console.error(`Invalid release uri ${linkIdentifier.FilePath}`);
        return undefined;
    }
    return { ...match.groups, releaseId: parseInt(match.groups.releaseId), environmentId: parseInt(match.groups.environmentId) };
}

Services.add(IReleaseLinkTypeServiceID, {
    serviceFactory: ReleaseLinkTypeService
});
